package com.dotdrives.web.shell

import com.dotdrives.model.OrgSummary
import react.FC
import react.Key
import react.Props
import react.createContext
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.label
import react.dom.html.ReactHTML.option
import react.dom.html.ReactHTML.select
import react.dom.html.ReactHTML.span
import react.use
import web.cssom.ClassName

data class OrganizationSelectionState(
    val organizations: List<OrgSummary>,
    val activeOrgId: String?,
    val onSelect: (String) -> Unit,
)

val OrganizationSelection = createContext<OrganizationSelectionState?>(null)

/** Duplicate legacy organization names need enough context to identify each account. */
internal fun organizationOptionLabels(organizations: List<OrgSummary>): List<Pair<String, String>> {
    val distinct = organizations.distinctBy { it.id }
    return distinct.sortedWith(compareBy<OrgSummary> { it.name.lowercase() }.thenBy { it.id }).map { org ->
        val sameName = distinct.filter { it.name.trim().equals(org.name.trim(), ignoreCase = true) }
        val label = if (sameName.size == 1) org.name else {
            val role = org.role.replace('_', ' ').replaceFirstChar { it.uppercase() }
            val sameRole = sameName.filter { it.role.equals(org.role, ignoreCase = true) }
            if (sameRole.size == 1) "${org.name} · $role" else {
                val prefixLength = (6..org.id.length).firstOrNull { length ->
                    sameRole.count { it.id.take(length) == org.id.take(length) } == 1
                } ?: org.id.length
                "${org.name} · $role · ${org.id.take(prefixLength)}"
            }
        }
        org.id to label
    }
}

val OrganizationSelector = FC<Props> {
    val selection = use(OrganizationSelection)
    if (selection != null && selection.organizations.isNotEmpty()) {
        val options = organizationOptionLabels(selection.organizations)
        val activeName = selection.organizations.firstOrNull { it.id == selection.activeOrgId }?.name
        div {
            className = ClassName("portal-organization-selector")
            if (options.size == 1) {
                span { className = ClassName("portal-organization-label"); +"Organization" }
                div { className = ClassName("portal-organization-name"); +options.single().second }
            } else label {
                span { className = ClassName("portal-organization-label"); +"Organization" }
                select {
                    className = ClassName("portal-organization-select")
                    ariaLabel = "Organization"
                    value = selection.activeOrgId?.takeIf { activeName != null } ?: ""
                    title = options.firstOrNull { it.first == selection.activeOrgId }?.second ?: "Choose organization"
                    onChange = { event ->
                        val id = event.currentTarget.value
                        if (id != selection.activeOrgId && selection.organizations.any { it.id == id }) selection.onSelect(id)
                    }
                    if (activeName == null) option { value = ""; disabled = true; +"Choose organization" }
                    options.forEach { (id, text) -> option { key = id.unsafeCast<Key>(); value = id; +text } }
                }
            }
        }
    }
}
